import { useMutation } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import { Clock, X } from 'lucide-react';
import type { PromisedTimeSuggestion } from '@/lib/api/tickets.js';
import { updateTicket } from '@/lib/api/tickets.js';
import { formatDateTime } from '@/lib/format.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';

function confidenceLabel(level: PromisedTimeSuggestion['confidenceLevel']): string {
  if (level === 'high') return '🟢 High confidence';
  if (level === 'medium') return '🟡 Medium confidence';
  return '🔴 Low confidence';
}

export function PromisedTimeSuggestionModal({
  open,
  ticketId,
  suggestion,
  onClose,
  onApplied,
}: {
  open: boolean;
  ticketId: string;
  suggestion: PromisedTimeSuggestion | null;
  onClose: () => void;
  onApplied: () => void;
}) {
  const applyMut = useMutation({
    mutationFn: () =>
      updateTicket(ticketId, { promisedDate: suggestion!.suggestedAt }),
    onSuccess: () => {
      toastSuccess('Promised time updated');
      onApplied();
      onClose();
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  if (!open || !suggestion) return null;

  const { breakdown } = suggestion;
  const totalHours = Math.floor(breakdown.totalMinutes / 60);
  const totalMins = breakdown.totalMinutes % 60;
  const totalLabel =
    totalHours > 0 ? `${totalHours}h${totalMins > 0 ? ` ${totalMins}m` : ''}` : `${totalMins} min`;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button type="button" className="absolute inset-0 bg-slate-950/40" aria-label="Close" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-bold text-slate-950">Suggested Pickup Time</h2>
          </div>
          <button type="button" className="rounded-lg p-1 text-slate-400 hover:bg-slate-100" onClick={onClose}>
            <X className="h-5 w-5" />
          </button>
        </div>

        <p className="text-xl font-semibold text-slate-950">{formatDateTime(suggestion.suggestedAt)}</p>
        <p className="mt-1 text-sm text-slate-600">{confidenceLabel(suggestion.confidenceLevel)}</p>
        {suggestion.reasoning ? (
          <p className="mt-2 text-sm text-slate-500">{suggestion.reasoning}</p>
        ) : null}

        <dl className="mt-4 grid grid-cols-2 gap-3 rounded-xl bg-slate-50 p-4 text-sm sm:grid-cols-4">
          <div>
            <dt className="text-slate-500">Repair</dt>
            <dd className="font-semibold">{breakdown.repairMinutes} min</dd>
          </div>
          <div>
            <dt className="text-slate-500">Queue wait</dt>
            <dd className="font-semibold">{breakdown.queueWaitMinutes} min</dd>
          </div>
          <div>
            <dt className="text-slate-500">Buffer</dt>
            <dd className="font-semibold">{breakdown.bufferMinutes} min</dd>
          </div>
          <div>
            <dt className="text-slate-500">Total</dt>
            <dd className="font-semibold">{totalLabel}</dd>
          </div>
        </dl>

        <div className="mt-6 flex gap-2">
          <button
            type="button"
            className="flex-1 rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            onClick={onClose}
          >
            Keep Current
          </button>
          <button
            type="button"
            disabled={applyMut.isPending}
            className="flex-1 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#164477] disabled:opacity-50"
            onClick={() => applyMut.mutate()}
          >
            {applyMut.isPending ? 'Saving…' : 'Use Suggested Time'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
